import { Component, Inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import {
  PRICE_GROUP_EDITABLE_FIELDS,
  PriceGroupValues,
  PriceGroupRecord,
} from "./price-group.model";
import { PriceGroupsService } from "./price-groups.service";
import { finalize } from "rxjs";
import { ToastService } from "../../_services/toast.service";

type TextField = Exclude<keyof PriceGroupValues, "is_active">;
export interface PriceGroupEditorData {
  mode: "add" | "view" | "edit";
  record?: PriceGroupRecord;
}

@Component({
  selector: "app-price-group-dialog",
  standalone: true,
  imports: [CommonModule, MatDialogModule, ReactiveFormsModule],
  templateUrl: "./price-group-dialog.component.html",
  styleUrls: ["./price-group-dialog.component.scss"],
})
export class PriceGroupDialogComponent {
  readonly fields: { key: TextField; label: string; arabic?: boolean }[] = [
    { key: "sap_price_group_code", label: "Price group code" },
    { key: "price_group_en", label: "Price group name in English" },
    {
      key: "price_group_ar",
      label: "Price group name in Arabic",
      arabic: true,
    },
    {
      key: "display_price_group_en",
      label: "Display price group name in English",
    },
    {
      key: "display_price_group_ar",
      label: "Display price group name in Arabic",
      arabic: true,
    },
    { key: "company_code", label: "Company code" },
    { key: "sales_organization", label: "Sales organization" },
  ];
  readonly form: FormGroup;
  saving = false;
  error = "";
  get isView(): boolean {
    return this.data.mode === "view";
  }
  get isEdit(): boolean {
    return this.data.mode === "edit";
  }
  get title(): string {
    return this.isView
      ? "Price Group Details"
      : this.isEdit
        ? "Edit Price Group"
        : "Add Price Group";
  }
  isEditableField(key: TextField): boolean {
    return !this.isView;
  }
  get hasChanges(): boolean {
    return (
      this.fields.some(
        (field) =>
          String(this.form.get(field.key).value).trim() !==
          this.data.record?.[field.key],
      ) || this.form.get("is_active").value !== this.data.record?.is_active
    );
  }
  get approvalDescription(): string {
    switch (this.data.record?.approvalStatus) {
      case "pending":
        return "Awaiting admin review.";
      case "approved":
        return "This price group has been approved.";
      case "rejected":
        return "This price group was rejected.";
      default:
        return "Approval status is unavailable.";
    }
  }
  get approvalIcon(): string {
    switch (this.data.record?.approvalStatus) {
      case "pending":
        return "schedule";
      case "approved":
        return "check_circle";
      case "rejected":
        return "cancel";
      default:
        return "help_outline";
    }
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PriceGroupEditorData,
    private dialogRef: MatDialogRef<PriceGroupDialogComponent>,
    private priceGroups: PriceGroupsService,
    private toast: ToastService,
  ) {
    const controls: Record<string, FormControl> = {};
    for (const field of this.fields) {
      controls[field.key] = new FormControl(data.record?.[field.key] || "", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/\S/),
          Validators.maxLength(150),
        ],
      });
    }
    controls.is_active = new FormControl(data.record?.is_active ?? true, {
      nonNullable: true,
    });
    this.form = new FormGroup(controls);
    if (this.isView) this.form.disable();
  }

  save(): void {
    this.form.markAllAsTouched();
    if (this.isView || this.saving) return;
    if (this.form.invalid) {
      this.toast.displayToast(
        "Please correct the highlighted fields before submitting.",
        "right",
        "warning",
      );
      return;
    }
    if (this.isEdit && (!this.data.record || !this.hasChanges)) {
      this.toast.displayToast(
        "Make a change before saving.",
        "right",
        "warning",
      );
      return;
    }
    const value = this.form.getRawValue();
    for (const field of this.fields) value[field.key] = value[field.key].trim();
    this.saving = true;
    this.error = "";
    this.dialogRef.disableClose = true;
    const changes: Partial<PriceGroupValues> = {};
    if (this.isEdit) {
      for (const key of PRICE_GROUP_EDITABLE_FIELDS) {
        if (value[key] !== this.data.record[key])
          Object.assign(changes, { [key]: value[key] });
      }
    }
    const request = this.isEdit
      ? this.priceGroups.update(this.data.record.id, changes)
      : this.priceGroups.create(value as PriceGroupValues);
    request
      .pipe(
        finalize(() => {
          this.saving = false;
          this.dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (error) => {
          this.error =
            error?.error?.message ||
            error?.message ||
            `Unable to ${this.isEdit ? "update" : "create"} price group. Please try again.`;
          this.toast.displayToast(this.error, "right", "danger");
        },
      });
  }
}
